/**
 * Shared webcam helpers for customer photo capture.
 *
 * Background: customer photos were saving as valid-but-useless JPEGs — a black
 * 640x480 frame with a "camera disabled" icon in the middle. The upload path
 * was fine; the camera device itself was handing the browser a synthetic
 * placeholder frame and the app captured it faithfully, because nothing:
 *
 *   1. chose a real camera (plain `{ video: true }` takes the system default,
 *      which can be a virtual/stub device),
 *   2. waited for the video to actually produce a frame before allowing a
 *      capture (a 0x0 canvas encodes to "data:," -> a 0-byte upload the server
 *      silently drops),
 *   3. surfaced getUserMedia failures (they were swallowed by empty catches),
 *   4. looked at the captured pixels before uploading them.
 *
 * These helpers cover all four and are shared by CustomerForm (registration)
 * and CustomerProfile (photo retake), which both write to `customer/<tagId>/`.
 */

// Substrings that identify virtual / software cameras. These enumerate
// alongside real webcams and frequently sort first, which is how a stub
// device ends up as the default capture source.
const VIRTUAL_CAMERA_HINTS = [
  'virtual',
  'obs',
  'droidcam',
  'snap camera',
  'manycam',
  'vcam',
  'splitcam',
  'e2esoft',
  'xsplit',
  'camo',
  'iriun',
  'epoccam',
  'ndi',
  'dummy',
  'screen capture',
  'capture card',
];

export const isLikelyVirtualCamera = (label) => {
  const l = (label || '').toLowerCase();
  if (!l) return false;
  return VIRTUAL_CAMERA_HINTS.some((hint) => l.includes(hint));
};

/**
 * Video inputs with real cameras ranked ahead of virtual ones. OS ordering is
 * preserved inside each group. Labels are only populated once camera
 * permission has been granted, so call this after a successful getUserMedia.
 */
export const listVideoInputs = async () => {
  if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
    return [];
  }
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const cams = devices.filter((d) => d.kind === 'videoinput');
    const real = cams.filter((d) => !isLikelyVirtualCamera(d.label));
    const virtual = cams.filter((d) => isLikelyVirtualCamera(d.label));
    return [...real, ...virtual];
  } catch (err) {
    return [];
  }
};

/**
 * Opens a camera stream, trying the most specific constraint first and
 * loosening on failure. A permission denial short-circuits — retrying with a
 * looser constraint cannot fix it and only prompts the employee again.
 */
export const openCameraStream = async (options) => {
  const opts = options || {};
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    const err = new Error('getUserMedia unavailable');
    err.name = 'NotSupportedError';
    throw err;
  }

  const attempts = [];
  if (opts.deviceId) {
    attempts.push({ audio: false, video: { deviceId: { exact: opts.deviceId } } });
  }
  if (opts.facingMode) {
    attempts.push({
      audio: false,
      video: { facingMode: { ideal: opts.facingMode } },
    });
  }
  attempts.push({ audio: false, video: true });

  let lastErr = null;
  for (let i = 0; i < attempts.length; i += 1) {
    try {
      /* eslint-disable-next-line no-await-in-loop */
      return await navigator.mediaDevices.getUserMedia(attempts[i]);
    } catch (err) {
      lastErr = err;
      const name = err && err.name;
      if (name === 'NotAllowedError' || name === 'SecurityError') throw err;
    }
  }
  if (lastErr) throw lastErr;
  const err = new Error('No camera available');
  err.name = 'NotFoundError';
  throw err;
};

/** Turns a getUserMedia rejection into something an employee can act on. */
export const describeCameraError = (err) => {
  const name = (err && err.name) || '';
  switch (name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return 'Camera blocked. Click the camera icon in the address bar and allow camera access for this site, then try again.';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return 'No camera found. Check that the webcam is plugged in and that camera access is turned on in Windows privacy settings.';
    case 'NotReadableError':
    case 'TrackStartError':
      return 'The camera could not be started — another program may be using it, or a privacy shutter/switch is closed. Close other apps using the camera and try again.';
    case 'OverconstrainedError':
    case 'ConstraintNotSatisfiedError':
      return 'That camera is no longer available. Pick a different camera and try again.';
    case 'AbortError':
      return 'The camera stopped unexpectedly. Try again.';
    case 'NotSupportedError':
      return 'This browser cannot access the camera. Use Chrome over HTTPS.';
    case 'CameraTimeoutError':
      return 'The camera opened but never sent a picture. It may be a virtual camera or have a closed privacy shutter — pick a different camera and try again.';
    default:
      return `Camera not available${name ? ` (${name})` : ''}.`;
  }
};

/**
 * Resolves once the element is actually producing frames. Without this, a
 * capture fired immediately after "Open Camera" reads videoWidth === 0 and
 * encodes an empty canvas.
 */
export const waitForVideoReady = (video, timeoutMs = 8000) =>
  new Promise((resolve, reject) => {
    if (!video) {
      reject(new Error('Video element unavailable'));
      return;
    }
    const isReady = () =>
      video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0;
    if (isReady()) {
      resolve();
      return;
    }
    let timer = null;
    const onEvent = () => {
      if (isReady()) {
        /* eslint-disable-next-line no-use-before-define */
        cleanup();
        resolve();
      }
    };
    const cleanup = () => {
      video.removeEventListener('loadedmetadata', onEvent);
      video.removeEventListener('loadeddata', onEvent);
      video.removeEventListener('canplay', onEvent);
      if (timer) clearTimeout(timer);
    };
    video.addEventListener('loadedmetadata', onEvent);
    video.addEventListener('loadeddata', onEvent);
    video.addEventListener('canplay', onEvent);
    timer = setTimeout(() => {
      cleanup();
      if (isReady()) {
        resolve();
        return;
      }
      const err = new Error('Camera produced no video');
      err.name = 'CameraTimeoutError';
      reject(err);
    }, timeoutMs);
  });

/** Attaches a stream and waits until it is genuinely playable. */
export const attachStream = async (video, stream) => {
  /* eslint-disable no-param-reassign */
  video.srcObject = stream;
  video.muted = true;
  video.playsInline = true;
  /* eslint-enable no-param-reassign */
  try {
    const p = video.play();
    if (p && p.catch) await p;
  } catch (err) {
    // An autoplay rejection is not fatal on its own — if frames still arrive
    // the readiness wait below succeeds. If they don't, that wait reports it.
  }
  await waitForVideoReady(video);
};

/** Stops every track on a video element and detaches the stream. */
export const stopStream = (video) => {
  if (!video) return;
  const stream = video.srcObject;
  if (stream && stream.getTracks) {
    stream.getTracks().forEach((track) => {
      try {
        track.stop();
      } catch (err) {
        /* already stopped */
      }
    });
  }
  /* eslint-disable-next-line no-param-reassign */
  video.srcObject = null;
};

/**
 * Rejects frames that carry no photographic content.
 *
 * The reference bad frame measured 96.4% *exactly* (0,0,0) pixels with a
 * pure-white icon in the middle — a rendered placeholder, not a sensor image.
 * A real webcam frame always carries a noise floor, so an overwhelming share
 * of mathematically pure black is the reliable tell; a plain variance test
 * misses it because the white icon supplies variance. The flat-frame and
 * low-variance checks below catch solid-colour frames from other stub drivers.
 */
export const analyzeFrame = (ctx, width, height) => {
  const total = width * height;
  if (!total) {
    return { ok: false, reason: 'The camera returned an empty frame.' };
  }

  let data;
  try {
    data = ctx.getImageData(0, 0, width, height).data;
  } catch (err) {
    // Tainted canvas or a locked-down browser — never block a save over an
    // inspection we were not allowed to perform.
    return { ok: true, reason: '' };
  }

  const stride = Math.max(1, Math.floor(total / 20000));
  let n = 0;
  let sum = 0;
  let sumSq = 0;
  let pureBlack = 0;
  let pureWhite = 0;

  for (let p = 0; p < total; p += stride) {
    const i = p * 4;
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    if (r <= 2 && g <= 2 && b <= 2) pureBlack += 1;
    else if (r >= 253 && g >= 253 && b >= 253) pureWhite += 1;
    const lum = 0.299 * r + 0.587 * g + 0.114 * b;
    sum += lum;
    sumSq += lum * lum;
    n += 1;
  }

  const mean = sum / n;
  const sd = Math.sqrt(Math.max(0, sumSq / n - mean * mean));
  const blackShare = pureBlack / n;
  const flatShare = (pureBlack + pureWhite) / n;
  const stats = { sd, blackShare, flatShare };

  if (blackShare >= 0.9) {
    return {
      ok: false,
      stats,
      reason:
        'The camera sent a blank "camera off" frame instead of a picture. The lens cover / privacy shutter may be closed, or Chrome may be using a virtual camera — pick a different camera below and try again.',
    };
  }
  if (flatShare >= 0.95) {
    return {
      ok: false,
      stats,
      reason:
        'The camera sent a blank frame instead of a picture. Check the camera and try again.',
    };
  }
  if (sd < 3) {
    return {
      ok: false,
      stats,
      reason:
        'The camera image is flat and featureless — it is not showing a real picture. Check the lighting and the lens cover, then try again.',
    };
  }
  return { ok: true, reason: '', stats };
};

/**
 * Draws the current video frame to the canvas and encodes it, inspecting the
 * pixels first. `dataURL` is returned even when `ok` is false so the caller can
 * still offer a deliberate "use anyway" override.
 */
export const captureFrame = (video, canvas, options) => {
  const opts = options || {};
  const quality = typeof opts.quality === 'number' ? opts.quality : 0.5;
  if (!video || !canvas) {
    return { ok: false, dataURL: null, reason: 'Camera is not ready yet.' };
  }
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) {
    return {
      ok: false,
      dataURL: null,
      reason:
        'The camera has not sent a picture yet. Wait for the preview to appear, then capture.',
    };
  }

  const ctx = canvas.getContext('2d');
  if (opts.square) {
    const size = Math.min(vw, vh);
    canvas.width = size;
    canvas.height = size;
    ctx.drawImage(video, (vw - size) / 2, (vh - size) / 2, size, size, 0, 0, size, size);
  } else {
    canvas.width = vw;
    canvas.height = vh;
    ctx.drawImage(video, 0, 0, vw, vh);
  }

  const check = analyzeFrame(ctx, canvas.width, canvas.height);
  const dataURL = canvas.toDataURL('image/jpeg', quality);

  // A 0x0 / failed encode yields a stub data URL with no payload. Never let
  // that reach the uploader — the server drops empty parts and the employee
  // is left thinking the photo saved.
  if (!dataURL || dataURL.length < 100 || dataURL.indexOf('data:image/jpeg') !== 0) {
    return {
      ok: false,
      dataURL: null,
      reason: 'The photo could not be encoded. Try capturing again.',
    };
  }

  return { ok: check.ok, dataURL, reason: check.reason, stats: check.stats };
};

/** data URL -> File, with the shared "{timestamp}{rand}.jpg" naming. */
export const dataURLToPhotoFile = async (dataURL) => {
  const res = await fetch(dataURL);
  const blob = await res.blob();
  if (!blob || !blob.size) {
    throw new Error('The captured photo was empty.');
  }
  const file = new File(
    [blob],
    `${Date.now() + Math.floor(Math.random() * 10000)}.jpg`,
    { type: 'image/jpeg' }
  );
  return {
    localURL: URL.createObjectURL(file),
    displayName: file.name,
    type: file.type,
    file,
  };
};
